import { find } from 'lodash';
import React, { Fragment, useCallback, useMemo, useState } from 'react';

import { payoutOrdersApi } from 'api';
import {
  AmountErrorDialog,
  AmountErrorDialogData,
  FormikSelectOption,
  PaginationWrapper,
} from 'components';
import { TIME_INTERVAL } from 'constants/time-interval.constants';
import { AdditionalOrderColumn, OrdersTab } from 'enums';
import { usePartialQuery, useQueryFilters, useUser } from 'hooks';
import { FilterDefinition, PayoutOrder, PayoutOrderFilters } from 'types';
import { orderUtils } from 'utils';

import { OrderListWithAction } from '../OrderList';
import { OrdersActions } from '../OrdersActions';

type Props = {
  tab: OrdersTab;
  additionalColumns?: AdditionalOrderColumn[];
  additionalFilters?: FilterDefinition[];
  additionalSortingFields?: FormikSelectOption[];
};

export const Orders: React.FC<Props> = ({
  tab,
  additionalFilters = [],
  additionalColumns = [],
  additionalSortingFields = [],
}) => {
  const { role, apiRole } = useUser();
  const { filters } = useQueryFilters<PayoutOrderFilters>();
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const refetch = useMemo(() => tab === OrdersTab.Active, [tab]);

  const [amountErrorDialogProps, setAmountErrorDialogProps] = useState<{
    open: boolean;
    data?: AmountErrorDialogData;
  }>({ open: false });

  const handleAmountErrorDialogOpen = useCallback(
    (data: AmountErrorDialogData) => {
      setAmountErrorDialogProps({ open: true, data });
    },
    [],
  );

  const handleAmountErrorDialogClose = useCallback(() => {
    setAmountErrorDialogProps({ open: false });
  }, []);

  const queryKey = useMemo(() => `${tab}-payout-orders`, [tab]);
  const queryResult = usePartialQuery(
    queryKey,
    payoutOrdersApi.getAllPaginatedAsRole(apiRole, tab),
    {
      useCachedCount: true,
      ...(refetch && {
        refetchInterval: TIME_INTERVAL.FIFTEEN_SECONDS,
        refetchIntervalInBackground: true,
      }),
    },
  );

  const selectedOrder: PayoutOrder | null = useMemo(
    () =>
      find(queryResult.data?.items, (order) => order.id === selectedOrderId) ||
      null,
    [selectedOrderId, queryResult.data],
  );

  return (
    <Fragment>
      <OrdersActions
        tab={tab}
        queryResult={queryResult}
        sortingFields={[
          ...orderUtils.getCommonPayoutOrderSortingFields(),
          ...additionalSortingFields,
        ]}
        filtersDefinitions={[
          ...orderUtils.getCommonPayoutOrderFilters(filters),
          ...additionalFilters,
        ]}
      />
      <PaginationWrapper
        queryResult={queryResult}
        ignoreState={{ refetchLoading: refetch }}
      >
        <OrderListWithAction
          tab={tab}
          selectedOrder={selectedOrder}
          items={queryResult.data?.items || []}
          queryKey={queryKey}
          handleOrderSelection={setSelectedOrderId}
          columns={orderUtils.getPayoutOrderColumns(additionalColumns, {
            queryKey,
            role,
            handleAmountErrorDialogOpen,
          })}
          handleAmountErrorDialogOpen={handleAmountErrorDialogOpen}
        />
      </PaginationWrapper>
      <AmountErrorDialog
        {...amountErrorDialogProps}
        onClose={handleAmountErrorDialogClose}
      />
    </Fragment>
  );
};
